import { useRef, useEffect, useState } from "react";
import { useDisplay } from "@/context/DisplayContext";
import { ChevronDown } from "lucide-react";

export function CollapsibleSection({ id, title, headerRight, tintClass, children }) {
  const { toggleSection, isSectionCollapsed } = useDisplay();
  const collapsed = isSectionCollapsed(id);
  const bodyRef = useRef(null);
  const [height, setHeight] = useState("auto");

  useEffect(() => {
    if (bodyRef.current) {
      if (!collapsed) {
        setHeight(`${bodyRef.current.scrollHeight}px`);
      } else {
        setHeight(`${bodyRef.current.scrollHeight}px`);
        requestAnimationFrame(() => setHeight("0px"));
      }
    }
  }, [collapsed]);

  // On content change, recalc height if expanded
  useEffect(() => {
    if (!collapsed && bodyRef.current) {
      const timer = setTimeout(() => setHeight("auto"), 300);
      return () => clearTimeout(timer);
    }
  }, [collapsed, children]);

  const headerBg = tintClass || "bg-gray-50 hover:bg-gray-100";

  return (
    <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid={`section-${id}`}>
      <button
        onClick={() => toggleSection(id)}
        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${headerBg}`}
        style={{ borderBottom: collapsed ? "none" : "1px solid var(--border-color, #E5E7EB)" }}
        data-testid={`toggle-section-${id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
            strokeWidth={1.5}
          />
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600 truncate">
            {title}
          </h3>
        </div>
        {headerRight && (
          <div className="flex items-center gap-4 text-xs font-mono-data shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
      </button>
      <div
        ref={bodyRef}
        style={{
          maxHeight: collapsed ? "0px" : height,
          opacity: collapsed ? 0 : 1,
          overflow: "hidden",
          transition: "max-height 0.3s ease-out, opacity 0.2s ease",
        }}
      >
        {children}
      </div>
    </div>
  );
}
